import { envConfig } from "../../config/env.config";
import {
  IBasePagingRes,
  IBaseResponseModel
} from "../../models/common/models.type";
import {
  IGetTaskPagesReq,
  ITask,
  ITaskCard,
  ITaskCardCreateRequest,
  ITaskCardPage,
  ITaskCardTransitionRequest,
  ITaskCardUpdatePositionRequest,
  ITaskCardUpdateRequest,
  ITaskUpdateAssignRequest,
  ITaskUpdatePositionResponse,
  ITaskStatistics,
  ITaskStatisticsResponse,
  IUserTaskCount,
  ITaskByMonthResponse,
  ITaskCardStatistics,
  IWorkflowStatistics,
  IBoardStatistics,
  ITaskUpdateDeadlinePesonalRequest
} from "../../models/Task";
import ApiEndpoints from "../remote/apiEndpoints";
import api from "../remote/baseApi";
import { ETaskStatus } from "../../models/common/models.enum";

const API_TASK = ApiEndpoints.TASK;
const baseURL = envConfig.API_ENDPOINT;

const createTask = (
  params: ITaskCardCreateRequest
): Promise<IBaseResponseModel<ITaskCard>> => {
  return api.post(baseURL, API_TASK.CREATE.url, params);
};

const updateTask = (
  params: ITaskCardUpdateRequest
): Promise<IBaseResponseModel<ITaskCard>> => {
  return api.put(baseURL, API_TASK.UPDATE.url, params);
};

const updateTaskTransition = (
  params: ITaskCardTransitionRequest
): Promise<IBaseResponseModel<ITaskCard>> => {
  return api.put(baseURL, API_TASK.UPDATE_TASK_TRANSITION.url, params);
};

const updateAssign = (
  params: ITaskUpdateAssignRequest
): Promise<IBaseResponseModel<ITaskCard>> => {
  return api.put(baseURL, API_TASK.UPDATE_ASSIGN.url, params);
};

const updateDeadlinePesonal = (
  params: ITaskUpdateDeadlinePesonalRequest
): Promise<IBaseResponseModel<ITaskCard>> => {
  return api.put(baseURL, API_TASK.UPDATE_DEADLINEPESONAL.url, params);
};

const getSingle = (id: string): Promise<IBaseResponseModel<ITask>> => {
  return api.get(baseURL, API_TASK.GET_SINGLE.url + `/${id}`);
};

const updateTaskPosition = ({
  TaskCardMoveId,
  WorkflowId,
  NewPosition
}: ITaskCardUpdatePositionRequest): Promise<
  IBaseResponseModel<ITaskUpdatePositionResponse>
> => {
  return api.put(baseURL, API_TASK.UPDATE_POSITION.url, {
    TaskCardMoveId: TaskCardMoveId,
    WorkflowId: WorkflowId,
    NewPosition: NewPosition
  });
};

const markAsFailed = (
  taskId: string,
  reasonFailed: string
): Promise<IBaseResponseModel<string>> => {
  return api.put(baseURL, API_TASK.MARK_AS_FAILED.url, {
    id: taskId,
    reasonFailed: reasonFailed
  });
};

const nextStage = (taskId: string): Promise<IBaseResponseModel<string>> => {
  return api.put(baseURL, API_TASK.NEXT_STAGE.url, {
    id: taskId
  });
};

const deleteTask = (id: string): Promise<IBaseResponseModel<string>> => {
  return api.delete(baseURL, API_TASK.DELETE.url + `/${id}`);
};

const getPage = (
  data: IGetTaskPagesReq
): Promise<IBasePagingRes<ITaskCardPage>> => {
  return api.get(baseURL, API_TASK.GET_PAGE.url, {
    params: data
  });
};

const getPageChildren = (
  data: IGetTaskPagesReq
): Promise<IBasePagingRes<ITaskCardPage>> => {
  return api.get(baseURL, API_TASK.GET_PAGE_CHILDREN.url, {
    params: data
  });
};

const rollBack = (id: string): Promise<IBasePagingRes<string>> => {
  return api.put(baseURL, API_TASK.ROLL_BACK.url, {
    id: id
  });
};
const updateStatus = (
  id: string,
  status: ETaskStatus
): Promise<IBaseResponseModel<string>> => {
  return api.put(baseURL, API_TASK.UPDATE_STATUS.url, {
    id: id,
    status: status
  });
};
const exportExcelTaskByBoardId = (
  id: string
): Promise<IBaseResponseModel<string>> => {
  return api.get(
    baseURL,
    API_TASK.EXPORT_EXCEL_TASK_BY_BOARD_ID.url + `/${id}`
  );
};

const getStatistics = (): Promise<
  IBaseResponseModel<ITaskStatisticsResponse>
> => {
  return api.get(baseURL, API_TASK.GET_STATISTICS.url);
};

const getTaskCountByUser = (): Promise<
  IBaseResponseModel<IUserTaskCount[]>
> => {
  return api.get(baseURL, API_TASK.GET_TASK_COUNT_BY_USER.url);
};

const getTaskByMonth = (): Promise<
  IBaseResponseModel<ITaskByMonthResponse>
> => {
  return api.get(baseURL, API_TASK.GET_TASK_BY_MONTH.url);
};

const getCompletedTaskByMonth = (): Promise<
  IBaseResponseModel<ITaskByMonthResponse>
> => {
  return api.get(baseURL, API_TASK.GET_COMPLETED_TASK_BY_MONTH.url);
};

const getTaskStatisticsByTaskCardId = (
  taskCardId: string
): Promise<IBaseResponseModel<ITaskCardStatistics>> => {
  return api.get(baseURL, API_TASK.GET_TASK_STATISTICS_BY_TASKCARD_ID.url, {
    params: { taskCardId }
  });
};

const getTaskStatisticsByWorkflowId = (
  workflowId: string
): Promise<IBaseResponseModel<IWorkflowStatistics>> => {
  return api.get(baseURL, API_TASK.GET_TASK_STATISTICS_BY_WORKFLOW_ID.url, {
    params: { workflowId }
  });
};

const getStatisticsForAdmin = (): Promise<
  IBaseResponseModel<ITaskStatisticsResponse>
> => {
  return api.get(baseURL, API_TASK.GET_STATISTICS_FOR_ADMIN.url);
};

const getTaskCountByUserForAdmin = (): Promise<
  IBaseResponseModel<IUserTaskCount[]>
> => {
  return api.get(baseURL, API_TASK.GET_TASK_COUNT_BY_USER_FOR_ADMIN.url);
};

const getTaskByMonthForAdmin = (): Promise<
  IBaseResponseModel<ITaskByMonthResponse>
> => {
  return api.get(baseURL, API_TASK.GET_TASK_BY_MONTH_FOR_ADMIN.url);
};

const getCompletedTaskByMonthForAdmin = (): Promise<
  IBaseResponseModel<ITaskByMonthResponse>
> => {
  return api.get(baseURL, API_TASK.GET_COMPLETED_TASK_BY_MONTH_FOR_ADMIN.url);
};

const getTaskStatisticsByWorkflowIdForAdmin = (
  workflowId: string
): Promise<IBaseResponseModel<IWorkflowStatistics>> => {
  return api.get(
    baseURL,
    API_TASK.GET_TASK_STATISTICS_BY_WORKFLOW_ID_FOR_ADMIN.url,
    {
      params: { workflowId }
    }
  );
};

const getTaskStatisticsByBoard = (
  boardId: string
): Promise<IBaseResponseModel<IBoardStatistics>> => {
  return api.get(baseURL, API_TASK.GET_TASK_STATISTICS_BY_BOARD.url, {
    params: { boardId }
  });
};

const getTaskStatisticsByBoardForAdmin = (
  boardId: string
): Promise<IBaseResponseModel<IBoardStatistics>> => {
  return api.get(baseURL, API_TASK.GET_TASK_STATISTICS_BY_BOARD_FOR_ADMIN.url, {
    params: { boardId }
  });
};

const TaskService = {
  createTask,
  updateTask,
  getSingle,
  updateTaskPosition,
  updateTaskTransition,
  updateAssign,
  deleteTask,
  getPage,
  getPageChildren,
  nextStage,
  markAsFailed,
  rollBack,
  updateStatus,
  exportExcelTaskByBoardId,
  getStatistics,
  getTaskCountByUser,
  getTaskByMonth,
  getCompletedTaskByMonth,
  getTaskStatisticsByTaskCardId,
  getTaskStatisticsByWorkflowId,
  getStatisticsForAdmin,
  getTaskCountByUserForAdmin,
  getTaskByMonthForAdmin,
  getCompletedTaskByMonthForAdmin,
  getTaskStatisticsByWorkflowIdForAdmin,
  getTaskStatisticsByBoard,
  getTaskStatisticsByBoardForAdmin,
  updateDeadlinePesonal
};

export default TaskService;
